import PropTypes from 'prop-types';
import { useState, useEffect, useCallback } from 'react';
import { Box } from '@mui/material';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { useNotificationContext } from 'contexts/NotificationContext';
import inputSignalsService from 'services/inputSignalsService';
import { formatDateTime } from 'utils/utils';
import AlertDetailSection from './AlertDetailSection';
import AlertActionDetails from './AlertActionDetails';
import DraftOutreachSection from '../../DraftOutreach/DraftOutreachSection';
import Drawer from '../../Common/Drawer/Drawer';
import salesforceTasksService from 'services/salesforceTasksService';
import SalesforceTaskCreateForm from '../../Tasks/SalesforceTaskCreateForm';
import { useIRWorkstation } from 'features/IRWorkstation/contexts/IRWorkstationContext';

// Track Events
import { useTrackEvent } from 'hooks/useTrackEvent';
import { ButtonClickTargets } from 'constants/buttonClickTargets';
import { PageLoadsTargets } from 'constants/pageLoadTargets';

// Canonical Salesforce API names the drafting endpoint accepts. It compares them
// exactly, but an alert's salesforce_object_type is not normalized backend-side
// (for rule-derived alerts the type comes straight from Salesforce report metadata
// via the rule's `select` payload), so it arrives as 'account' or 'Account'. Match
// case-insensitively here, then always send the canonical spelling.
const DRAFT_OUTREACH_OBJECT_TYPES = {
	account: 'Account',
	aim__fundraising__c: 'AIM__Fundraising__c',
};

// The Salesforce record a draft should be based on, or null when the alert points
// at something the drafting endpoint cannot use. The endpoint resolves the account
// itself (a fundraising via its AIM__Account__c lookup), so we just forward the
// alert's own type and id.
const resolveDraftOutreachTarget = (alert) => {
	const sf = alert?.salesforce_object;
	const type =
		DRAFT_OUTREACH_OBJECT_TYPES[sf?.salesforce_object_type?.toLowerCase()];

	if (!type || !sf?.salesforce_object_id) return null;

	return { type, id: sf.salesforce_object_id };
};

const AlertsDrawer = ({
	alert,
	tenant,
	open,
	onClose,
	onDataChanged,
	onAlertUpdated,
	onTaskCreated,
}) => {
	const { trackButtonClick, trackPageLoad } = useTrackEvent({ tenant, product: 'IEM' });

	const creationDatetime = formatDateTime(alert?.event_time || Date.now());

	const { featureFlags } = useIRWorkstation();

	const isDraftOutreachEnabled =
		featureFlags?.['enable-draft-outreach-prompt'] !== false;

	const draftOutreachTarget = resolveDraftOutreachTarget(alert);
	const isFailedState =
		alert?.event?.event_subcategory === 'stale_or_missing_data';
	// A failed alert has no record worth drafting from, whatever it points at.
	const canDraftOutreach = !isFailedState && !!draftOutreachTarget;

	const [updatingAcknowledgement, setUpdatingAcknowledgement] = useState(false);
	const [showCreateTaskForm, setShowCreateTaskForm] = useState(false);
	const [taskFormOptions, setTaskFormOptions] = useState(null);
	const [taskFormOptionsLoading, setTaskFormOptionsLoading] = useState(false);

	//  Child component can set footer actions for the drawer, enabling 'multi-step' ux
	const [createTaskFooterActions, setCreateTaskFooterActions] = useState(null);

	const eventContext = alert?.event?.event_context ?? {};
	const hasEventContext = Object.keys(eventContext).length > 0;
	const hasAcknowledgementInfo =
		alert?.acknowledged_at && alert?.acknowledged_by_name;

	const { showNotification } = useNotificationContext();

	const isAcknowledged = alert?.event?.event_status === 'acknowledged';

	const getAcknowledgementButtonLabel = (isAcknowledged, isUpdating) => {
		if (isUpdating) {
			return isAcknowledged ? 'Unacknowledging...' : 'Acknowledging...';
		}

		return isAcknowledged ? 'Unacknowledge' : 'Acknowledge';
	};

	const canCreateTask =
		!!alert?.id &&
		!!alert?.salesforce_object?.salesforce_object_id;

	const handleAcknowledge = async () => {
		const eventStatus = isAcknowledged ? 'unacknowledged' : 'acknowledged';
		handleButtonClickTrackEvent(eventStatus);

		try {
			setUpdatingAcknowledgement(true);

			const updatedAlert =
				await inputSignalsService.updateAcknowledgementStatus(
					alert.id,
					eventStatus
				);

			onAlertUpdated?.(updatedAlert);

			showNotification(`Alert ${eventStatus}`, 'success');
		} catch (error) {
			console.error(error);

			showNotification(
				isAcknowledged
					? 'Failed to unacknowledge alert'
					: 'Failed to acknowledge alert',
				'error'
			);
		} finally {
			setUpdatingAcknowledgement(false);
		}
	};

	const handleCreateTask = () => {
		setShowCreateTaskForm(true);
	};

	const handleCancelCreateTask = useCallback(() => {
		setShowCreateTaskForm(false);
	}, []);

	// Sync alert/task data after creation, falling back to a full refresh
	// when the response does not include the updated input signal
	const handleTaskCreated = useCallback(
		async (response) => {
			if (response?.input_signal) {
				await Promise.all([
					onAlertUpdated?.(response.input_signal),
					onTaskCreated?.(),
				]);
			} else {
				await onDataChanged?.();
			}

			setShowCreateTaskForm(false);
		},
		[onAlertUpdated, onTaskCreated, onDataChanged]
	);

	const handleActionLinkEvent = (label) => {
		if (label.includes('CRM')) {
			handleButtonClickTrackEvent('viewRecordLink');
		} else if (label === 'View Alert Center') {
			handleButtonClickTrackEvent('viewAlertCenter');
		}
	};

	const handleButtonClickTrackEvent = (targetKey) => {
		const targetMap = {
			close: ButtonClickTargets.AlertsDrawerClose,
			createTask: ButtonClickTargets.AlertsDrawerCreateTask,
			draftOutreachGenerate:
				ButtonClickTargets.AlertsDrawerDraftOutreachGenerate,
			draftOutreachApply: ButtonClickTargets.AlertsDrawerDraftOutreachApply,
			draftOutreachViewRecordLink:
				ButtonClickTargets.AlertsDrawerDraftOutreachViewRecordInCrm,
			draftOutreachCopy: ButtonClickTargets.AlertsDrawerDraftOutreachCopy,
			acknowledged: ButtonClickTargets.AlertsDrawerAcknowledge,
			unacknowledged: ButtonClickTargets.AlertsDrawerUnacknowledge,
			viewRecordLink: ButtonClickTargets.AlertsDrawerViewRecordInCrm,
			viewAlertCenter: ButtonClickTargets.AlertsDrawerViewAlertCenter,
		};

		const target = targetMap[targetKey];
		if (target) {
			trackButtonClick(target);
		}
	};

	const handleClose = () => {
		handleButtonClickTrackEvent('close');
		setShowCreateTaskForm(false);
		onClose();
	};

	// Preload task form options when task creation becomes available
	useEffect(() => {
		if (!open || !canCreateTask || taskFormOptions) {
			return;
		}

		// Prevent stale async results from updating the form after this effect is cleaned up
		let isCurrent = true;

		const loadTaskFormOptions = async () => {
			try {
				setTaskFormOptionsLoading(true);

				const options = await salesforceTasksService.getFormOptions();

				// The effect may have been cleaned up while waiting for the request
				// Ignore the result if this is no longer the current effect run
				if (!isCurrent) {
					return;
				}

				setTaskFormOptions(options);
			} catch (error) {
				console.error('Failed to preload Salesforce task form options:', error);
			} finally {
				if (isCurrent) {
					setTaskFormOptionsLoading(false);
				}
			}
		};

		loadTaskFormOptions();

		// Mark this effect run as stale when the component unmounts or
		// when a dependency changes and React reruns the effect
		return () => {
			isCurrent = false;
		};
	}, [open, canCreateTask, taskFormOptions]);

	useEffect(() => {
		trackPageLoad(PageLoadsTargets.AlertDrawer);
	}, []);

	// Buttons rendered in the drawer footer
	const createTaskActionButtons = createTaskFooterActions ?? [];	
	const defaultActionButtons = [
		{
			key: 'alert-action-acknowledge-button',
			label: getAcknowledgementButtonLabel(
				isAcknowledged,
				updatingAcknowledgement
			),
			onClick: handleAcknowledge,
			loading: updatingAcknowledgement,
			variant: 'secondary',
		},
		alert.salesforce_object?.salesforce_object_id && {
			key: 'alert-action-create-task-button',
			label: 'Create Task',
			onClick: handleCreateTask,
			variant: 'secondary',
		}
	].filter(Boolean);

	// append list of actions from alert to create buttons for them
	if (alert.actions) {
		const alertActions = alert.actions.filter(action => action.action_link).map((action, index) => {
			return {
				key: `alert-action-link-button-${index}`,
				label: action.action_title || 'Open Link',
				href: action.action_link,
				onClick: () => handleActionLinkEvent(action.action_title),
				variant: 'primary',
			}
		});
		defaultActionButtons.push(...alertActions);
	}

	const subtitle = alert?.event?.event_subtitle
		? `${creationDatetime} · ${alert.event.event_subtitle}`
		: creationDatetime;

	return (
		<Drawer
			open={open}
			onClose={handleClose}
			title={showCreateTaskForm ? 'Create Task' : alert?.event?.event_title}
			subtitle={!showCreateTaskForm ? subtitle : undefined}
			actionButtons={
				showCreateTaskForm ? createTaskActionButtons : defaultActionButtons
			}
		>
			{/* main content rendered as children */}
			{showCreateTaskForm ? (
				<SalesforceTaskCreateForm
					alert={alert}
					tenant={tenant}
					formOptions={taskFormOptions}
					formOptionsLoading={taskFormOptionsLoading}
					onBack={handleCancelCreateTask}
					onCancel={handleCancelCreateTask}
					onCreateTaskClick={() => {
						handleButtonClickTrackEvent('createTask');
					}}
					onCreated={handleTaskCreated}
					onFooterActionsChange={setCreateTaskFooterActions}
				/>
			) : (
				<Box
					sx={{
						padding: '16px 18px',
						display: 'flex',
						flexDirection: 'column',
						gap: '14px',
					}}
				>
					{alert.event?.event_detail &&
						alert.event?.event_subcategory !== 'stale_or_missing_data' && (
							<Box>{alert.event?.event_detail}</Box>
						)}

					{alert.event?.event_detail &&
						alert.event?.event_subcategory === 'stale_or_missing_data' && (
							<Box
								sx={{
									fontSize: '12px',
									fontWeight: 500,
									padding: '7px 14px',
									border: '1px solid red',
									borderRadius: '6px',
									color: 'status.red.text',
									backgroundColor: 'status.red.bg',
								}}
							>
								<div
									style={{
										display: 'flex',
										alignItems: 'center',
										height: '20px',
									}}
								>
									<WarningAmberOutlinedIcon
										sx={{
											fontSize: 14,
											marginTop: '7px',
										}}
									/>
									<p
										style={{
											paddingLeft: '7px',
											paddingTop: '5px',
											fontWeight: 'bold',
										}}
									>
										ALERT EXECUTION FAILED
									</p>
								</div>

								<p
									style={{
										paddingLeft: '21px',
										marginTop: '6px',
										marginBottom: '6px',
									}}
								>
									{alert.event?.event_detail}
								</p>
							</Box>
						)}

					{/* Details */}
					{(hasEventContext || hasAcknowledgementInfo) && (
						<AlertDetailSection
							event_context={eventContext}
							acknowledgedAt={alert.acknowledged_at}
							acknowledgedByName={alert.acknowledged_by_name}
						/>
					)}

					{/* Action details */}
					{alert.actions?.map(
						(action, index) =>
							action.action_title &&
							action.action_description && (
								<AlertActionDetails
									key={index}
									title={action.action_title}
									description={action.action_description}
								/>
							)
					)}

					{/* Draft Outreach */}
					{isDraftOutreachEnabled && canDraftOutreach && (
						<DraftOutreachSection
							tenantId={tenant?.id}
							salesforceObjectType={draftOutreachTarget.type}
							salesforceObjectId={draftOutreachTarget.id}
							salesforceOrgUrl={tenant?.salesforce_org_url}
							onGenerateClick={() => {
								handleButtonClickTrackEvent('draftOutreachGenerate');
							}}
							onApplyClick={() => {
								handleButtonClickTrackEvent('draftOutreachApply');
							}}
							onViewInCrmClick={() => {
								handleButtonClickTrackEvent('draftOutreachViewRecordLink');
							}}
							onCopyClick={() => {
								handleButtonClickTrackEvent('draftOutreachCopy');
							}}
						/>
					)}
				</Box>
			)}
		</Drawer>
	);
};

AlertsDrawer.propTypes = {
	alert: PropTypes.object,
	tenant: PropTypes.object,
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onDataChanged: PropTypes.func,
	onAlertUpdated: PropTypes.func,
	onTaskCreated: PropTypes.func,
};

export default AlertsDrawer;
